import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { remindPresets, whenLabel } from "../reminders";
import type { Message, Reminder } from "../sync/types";
import { RichText } from "../text";
import { PopMenu } from "./PopMenu";
import { TimePicker, TimeSheet } from "./TimePicker";
import { Avatar, Badge, Card } from "./ui";
import { t, tx } from "../i18n";

// RemindPicker asks when to be reminded about a message, from a menu of
// the usual times or a sheet, or with no message, what of and when. done
// says what happened.
export function RemindPicker({ anchor, message, onClose, done }: { anchor?: DOMRect; message?: Message; onClose: () => void; done: (said: string) => void }) {
  const session = useSession();
  async function set(at: Date, text = "") {
    try {
      await session.remind(at, message, text);
      done(t("I'll remind you {when}", { when: whenLabel(at) }));
    } catch (err) {
      done(err instanceof Error ? err.message : String(err));
    }
  }
  if (!message) return <NewReminder onClose={onClose} onSet={(at, text) => void set(at, text)} />;
  return (
    <TimePicker anchor={anchor} label={t("Remind me")} title={t("Remind me about this")} action={t("Set reminder")} onClose={onClose} onPick={(at) => void set(at)}>
      <p className="time-sheet-about">
        <strong>{session.name(message.author.id)}</strong>: {message.text ? <RichText text={message.text} name={(id) => session.name(id)} inline /> : t("a file")}
      </p>
    </TimePicker>
  );
}

// NewReminder asks what to be reminded of, and when.
function NewReminder({ onClose, onSet }: { onClose: () => void; onSet: (at: Date, text: string) => void }) {
  const [text, setText] = useState("");
  return (
    <TimeSheet title={t("New reminder")} action={t("Set reminder")} focusTime={false} onClose={onClose} onPick={(at) => (onClose(), onSet(at, text.trim()))}>
      <label>
        {t("Remind me to")}
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Send the release notes")} maxLength={500} required autoFocus />
      </label>
    </TimeSheet>
  );
}

// RemindersCard lists one's reminders on the inbox: those due, to mark
// done or snooze, and those to come, to cancel.
export function RemindersCard() {
  const session = useSession();
  const state = useLantern();
  const [adding, setAdding] = useState(false);
  const [said, setSaid] = useState("");
  const [snoozing, setSnoozing] = useState<{ id: string; anchor: DOMRect } | null>(null);
  useEffect(() => void session.loadReminders(), [session]);
  const list = state.reminders ?? [];
  const due = list.filter((r) => r.fired_at);
  const coming = list.filter((r) => !r.fired_at);

  async function act(f: () => Promise<unknown>) {
    setSaid("");
    try {
      await f();
    } catch (err) {
      setSaid(err instanceof Error ? err.message : String(err));
    }
    await session.loadReminders();
  }
  const row = (r: Reminder) => (
    <li key={r.id} className={r.fired_at ? "unread" : ""}>
      {r.message ? (
        <Avatar id={r.message.author.id} name={session.name(r.message.author.id)} size={36} />
      ) : (
        <span className="check-icon" aria-hidden>
          ⏰
        </span>
      )}
      <div className="row-main">
        <div className="inbox-kind">
          <Badge tone={r.fired_at ? "warn" : "neutral"}>{r.fired_at ? t("Reminder") : t("Reminder {when}", { when: whenLabel(new Date(r.remind_at)) })}</Badge>
        </div>
        {r.text && <p className="reminder-text">{r.text}</p>}
        {r.message ? (
          <button className="inbox-message" onClick={() => navigate(`/c/${r.message!.channel_id}/m/${r.message!.id}`)}>
            <span className="where">
              {tx("{who} in {channel}", { who: <strong>{session.name(r.message.author.id)}</strong>, channel: session.channelName(r.message.channel_id) })}
            </span>
            <span className="text">
              <RichText text={r.message.text} name={(id) => session.name(id)} me={state.me?.id} inline />
            </span>
          </button>
        ) : (
          r.message_id && <p className="muted">{t("The message is gone, or in a channel you left.")}</p>
        )}
      </div>
      <div className="reminder-actions">
        {r.fired_at ? (
          <>
            <button className="small" onClick={(e) => setSnoozing({ id: r.id, anchor: e.currentTarget.getBoundingClientRect() })}>
              {t("Snooze")}
            </button>
            <button className="small" onClick={() => void act(() => session.api.completeReminder(r.id))}>
              {t("Done")}
            </button>
          </>
        ) : (
          <button className="small" onClick={() => void act(() => session.api.completeReminder(r.id))} aria-label={t("Cancel reminder")}>
            {t("Cancel")}
          </button>
        )}
      </div>
    </li>
  );
  return (
    <Card
      title={t("Reminders")}
      actions={
        <button className="small" onClick={() => setAdding(true)}>
          {t("New reminder")}
        </button>
      }
    >
      {said && <p className="notice">{said}</p>}
      {list.length === 0 ? (
        <p className="muted">{t("Nothing to remember. Set a reminder from a message's More actions, or make one here.")}</p>
      ) : (
        <ul className="rows inbox-list reminder-list">
          {due.map(row)}
          {coming.map(row)}
        </ul>
      )}
      {adding && <RemindPicker onClose={() => setAdding(false)} done={setSaid} />}
      {snoozing && (
        <PopMenu
          anchor={snoozing.anchor}
          label={t("Snooze until")}
          onClose={() => setSnoozing(null)}
          items={remindPresets().map((p) => ({ label: p.label, run: () => void act(() => session.api.snoozeReminder(snoozing.id, p.at.toISOString())) }))}
        />
      )}
    </Card>
  );
}
